import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  FieldName,
  RequiredAddressFieldKeys,
  ValidationSectionKey,
  ValidationMessage,
} from '@/constants';
import { useCheckout } from '@/contexts/checkoutContext';
import { usePageData } from '@/contexts/pageContext';
import { useShipping } from '@/contexts/shippingContext';
import type { Address } from '@/types';
import { getRequiredAddressFields } from '@/utils/getRequiredAddressFields';
import { validatePhone } from '@/utils/validatePhone';
import type {
  AddressFieldErrors,
  ErrorMessages,
  ProviderProps,
  RequiredFormField,
  ValidationContextValue,
  ValidationSection,
} from './types';

type Validator = () => string;

type ValidatorsRegistry = {
  [field: string]: Validator;
};

interface ValidateAddressProps {
  address?: Address;
  addressType: 'billing' | 'delivery';
}

const initialErrorMessages = {} as ErrorMessages;

for (const value of Object.values(ValidationSectionKey)) {
  initialErrorMessages[value as keyof typeof initialErrorMessages] = '';
}

const initialAddressFieldErrors: Record<string, string> = {};

for (const value of Object.values(RequiredAddressFieldKeys)) {
  initialAddressFieldErrors[value] = '';
}

// As countyCode is occasionally required, depending on selected country,
// adding it to the initial errors object for more consistent validation behaviour.
initialAddressFieldErrors.countyCode = '';

const ValidationContext = createContext<ValidationContextValue | null>(null);

export const ValidationProvider = ({ children }: ProviderProps) => {
  const {
    checkoutData: { amounts },
    paymentOption,
  } = useCheckout();

  const { excludedCountriesForBilling } = usePageData();

  const totalAmount = amounts?.totalDecimal ?? 0;

  const {
    isBillingSameAsShipping,
    isClickAndCollect,
    isDelivery,
    orderPayload,
    shippingMethod,
  } = useShipping();

  const [errorMessages, setErrorMessages] =
    useState<ErrorMessages>(initialErrorMessages);

  const [addressFieldErrors, setAddressFieldErrors] =
    useState<AddressFieldErrors>({
      billing: initialAddressFieldErrors,
      delivery: initialAddressFieldErrors,
    });

  const validateAddress = useCallback(
    ({ address, addressType }: ValidateAddressProps) => {
      if (!address) {
        return ValidationMessage.Address.replace('${addressType}', addressType);
      }

      const requiredFields = getRequiredAddressFields(address.countryCode);

      const validationErrors: Record<string, string> = {};

      for (const field of requiredFields) {
        const value = address[field as keyof typeof address];

        if (value && typeof value === 'string') {
          // Phone validation: check format and total length
          if (field === FieldName.Phone) {
            if (!validatePhone(value)) {
              validationErrors[field] = ValidationMessage.InvalidPhone;
            } else if (value.length > 19) {
              validationErrors[field] = ValidationMessage.PhoneTooLong;
            }
          } else if (field === FieldName.FirstName && value.length > 20) {
            validationErrors[field] = ValidationMessage.FirstNameTooLong;
          } else if (field === FieldName.LastName && value.length > 20) {
            validationErrors[field] = ValidationMessage.LastNameTooLong;
          } else if (
            field === FieldName.AddressLine1 &&
            value.length > 50
          ) {
            validationErrors[field] = ValidationMessage.AddressLineTooLong;
          } else if (field === FieldName.City && value.length > 40) {
            validationErrors[field] = ValidationMessage.CityTooLong;
          } else if (field === FieldName.PostalCode && value.length > 10) {
            validationErrors[field] = ValidationMessage.PostalCodeTooLong;
          }
        } else {
          validationErrors[field] = ValidationMessage.MissingField;
        }
      }

      // Validate optional AddressLine2 length
      if (
        address.line2 &&
        typeof address.line2 === 'string' &&
        address.line2.length > 50
      ) {
        validationErrors[FieldName.AddressLine2] =
          ValidationMessage.AddressLineTooLong;
      }

      if (Object.keys(validationErrors).length > 0) {
        setAddressFieldErrors(previous => ({
          ...previous,
          [addressType]: validationErrors,
        }));

        return ValidationMessage.Address.replace('${addressType}', addressType);
      }

      return '';
    },
    [],
  );

  const validators: ValidatorsRegistry = useMemo(
    () => ({
      [ValidationSectionKey.BillingAddress]: () => {
        if (isDelivery && isBillingSameAsShipping) {
          // Check if shipping country is excluded from billing
          const shippingCountryCode = orderPayload.shippingAddress?.countryCode;
          if (
            shippingCountryCode &&
            excludedCountriesForBilling?.includes(shippingCountryCode)
          ) {
            return ValidationMessage.SeparateBillingRequired;
          }
          return '';
        }

        if (!isDelivery && !isClickAndCollect) {
          return '';
        }

        return validateAddress({
          address: orderPayload.billingAddress,
          addressType: 'billing',
        });
      },

      [ValidationSectionKey.DeliveryOption]: () =>
        isClickAndCollect || isDelivery
          ? ''
          : 'Please select a delivery option.',

      [ValidationSectionKey.Email]: () => {
        if (!orderPayload.email) {
          return 'Please enter an email address.';
        }
        if (orderPayload.email.length > 80) {
          return ValidationMessage.EmailTooLong;
        }
        return '';
      },

      [ValidationSectionKey.PaymentMethod]: () => {
        const nonZeroBalance = totalAmount !== 0;
        return nonZeroBalance && !paymentOption
          ? 'Please select a payment method.'
          : '';
      },

      [ValidationSectionKey.ShippingAddress]: () => {
        if (!isDelivery) {
          return '';
        }

        return validateAddress({
          address: orderPayload.shippingAddress,
          addressType: 'delivery',
        });
      },

      [ValidationSectionKey.ShippingMethod]: () =>
        isDelivery && !shippingMethod ? 'Please select a delivery method.' : '',

      [ValidationSectionKey.ClickAndCollectStoreId]: () =>
        isClickAndCollect && !orderPayload.clickAndCollectStoreId
          ? 'Please select a store location.'
          : '',
    }),
    [
      excludedCountriesForBilling,
      isBillingSameAsShipping,
      isClickAndCollect,
      isDelivery,
      orderPayload,
      paymentOption,
      shippingMethod,
      totalAmount,
      validateAddress,
    ],
  );

  const validatePayload = useCallback(() => {
    const newErrors = {} as ErrorMessages;

    for (const field of Object.keys(validators)) {
      const message = validators[field]();

      if (message) {
        newErrors[field as keyof typeof newErrors] = message;
      }
    }

    setErrorMessages(currentErrors => ({ ...currentErrors, ...newErrors }));

    const hasErrors = Object.values(newErrors).some(message => message !== '');

    return !hasErrors;
  }, [validators]);

  const validateSection = useCallback(
    (section: ValidationSection) => {
      setErrorMessages(currentMessages => ({
        ...currentMessages,
        [section]: validators[section](),
      }));
    },
    [validators],
  );

  const activeErrorMessage = Object.fromEntries(
    Object.entries(errorMessages).filter(([__dirname, value]) => value !== ''),
  );

  const clearValidationError = useCallback((section: ValidationSection) => {
    setErrorMessages(currentMessages => ({
      ...currentMessages,
      [section]: '',
    }));
  }, []);

  const setValidationError = useCallback(
    (section: ValidationSection, error: string) => {
      setErrorMessages(currentMessages => ({
        ...currentMessages,
        [section]: error,
      }));
    },
    [],
  );

  const clearAddressFieldError = useCallback(
    (addressType: 'billing' | 'delivery', field: RequiredFormField) => {
      setAddressFieldErrors(currentErrors => ({
        ...currentErrors,
        [addressType]: {
          ...currentErrors[addressType as keyof typeof currentErrors],
          [field]: '',
        },
      }));
    },
    [],
  );

  const setAddressFieldError = useCallback(
    (
      addressType: 'billing' | 'delivery',
      field: RequiredFormField,
      error: string,
    ) => {
      setAddressFieldErrors(currentErrors => ({
        ...currentErrors,
        [addressType]: {
          ...currentErrors[addressType as keyof typeof currentErrors],
          [field]: error,
        },
      }));
    },
    [],
  );

  const value = useMemo(
    () => ({
      addressFieldErrors,
      clearAddressFieldError,
      clearValidationError,
      errorMessages: activeErrorMessage,
      hasValidationError: Object.keys(activeErrorMessage).length > 0,
      setAddressFieldError,
      setValidationError,
      validatePayload,
      validateSection,
    }),
    [
      activeErrorMessage,
      addressFieldErrors,
      clearAddressFieldError,
      clearValidationError,
      setAddressFieldError,
      setValidationError,
      validatePayload,
      validateSection,
    ],
  );

  return (
    <ValidationContext.Provider value={value}>
      {children}
    </ValidationContext.Provider>
  );
};

export const useValidation = () => {
  const context = useContext(ValidationContext);

  if (context === undefined || context === null) {
    throw new Error(
      'useValidation must be used within a ValidationContext Provider',
    );
  }

  return context;
};
